/**
 * Slideshow Two Column - Custom carousel implementation
 * CSS animation drives slide changes for perfect sync
 */

class SlideshowTwoColumn extends HTMLElement {
  constructor() {
    super();
    
    this.currentIndex = 0;
    this.isAutoplayActive = false;
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    // Bind methods
    this.handleDotClick = this.handleDotClick.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.slideshow-two-column__slide'));
    this.navigation = document.querySelector(`carousel-navigation[aria-controls="${this.id}"]`);
    this.dots = this.navigation ? Array.from(this.navigation.querySelectorAll('button')) : [];
    
    // Etsi nuolipainikkeet (ne ovat navigation-wrapperin sisällä)
    const controlsWrapper = this.navigation?.parentElement;
    this.prevButton = controlsWrapper?.querySelector('.slideshow-two-column__arrow--prev');
    this.nextButton = controlsWrapper?.querySelector('.slideshow-two-column__arrow--next');
    
    console.log('🎯 Found prev button:', this.prevButton);
    console.log('🎯 Found next button:', this.nextButton);
    
    if (this.slides.length === 0) return;

    this.setupEventListeners();
    
    setTimeout(() => {
      this.initializeAutoplay();
    }, 100);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.removeEventListeners();
  }

  setupEventListeners() {
    console.log('🎧 Setting up event listeners');
    
    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.handleDotClick(index));
    });

    // Arrow navigation
    if (this.prevButton) {
      console.log('✅ Adding prev button listener');
      this.prevButton.addEventListener('click', this.handlePrevClick);
    } else {
      console.warn('⚠️ Prev button not found');
    }
    
    if (this.nextButton) {
      console.log('✅ Adding next button listener');
      this.nextButton.addEventListener('click', this.handleNextClick);
    } else {
      console.warn('⚠️ Next button not found');
    }

    // Swipe support
    if (this.hasAttribute('allow-swipe')) {
      this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      this.addEventListener('touchend', this.handleTouchEnd);
    }

    // Pause on hover
    if (this.hasAttribute('autoplay')) {
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
    }
    
    // Kuuntele CSS-animaation päättymistä
    if (this.navigation && this.hasAttribute('autoplay')) {
      this.navigation.addEventListener('animationend', this.handleAnimationEnd);
    }
  }

  removeEventListeners() {
    this.dots.forEach((dot, index) => {
      dot.removeEventListener('click', () => this.handleDotClick(index));
    });
    
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevClick);
    }
    
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextClick);
    }
    
    if (this.hasAttribute('allow-swipe')) {
      this.removeEventListener('touchstart', this.handleTouchStart);
      this.removeEventListener('touchmove', this.handleTouchMove);
      this.removeEventListener('touchend', this.handleTouchEnd);
    }

    if (this.hasAttribute('autoplay')) {
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
    }
    
    if (this.navigation) {
      this.navigation.removeEventListener('animationend', this.handleAnimationEnd);
    }
  }

  initializeAutoplay() {
    if (this.hasAttribute('autoplay') && this.slides.length > 1) {
      const delay = parseInt(this.getAttribute('autoplay')) * 1000 || 6000;
      this.autoplayDelay = delay;
      this.startAutoplay();
    }
  }

  startAutoplay() {
    if (this.isAutoplayActive) return;
    
    this.isAutoplayActive = true;

    // Aseta CSS-muuttuja
    if (this.navigation) {
      this.navigation.style.setProperty('--autoplay-duration', `${this.autoplayDelay}ms`);
      
      // Käynnistä CSS-animaatio
      const activeButton = this.navigation.querySelector('button[aria-current="true"]');
      const progressCircle = activeButton?.querySelector('.circular-progress circle:last-child');
      if (progressCircle) {
        progressCircle.style.animation = 'none';
        void progressCircle.offsetHeight; // Force reflow
        progressCircle.style.animation = '';
      }
    }
  }

  stopAutoplay() {
    if (!this.isAutoplayActive) return;
    this.isAutoplayActive = false;
  }

  handleAnimationEnd(event) {
    // Tarkista että kyseessä on progress-ring animaatio
    if (event.animationName === 'progress-ring' && this.isAutoplayActive) {
      this.goToNextSlide();
    }
  }

  handleDotClick(index) {
    console.log('🔘 Dot clicked:', index);
    this.goToSlide(index);
  }

  handlePrevClick(event) {
    event.preventDefault();
    console.log('⬅️ Prev button clicked');
    this.goToPreviousSlide();
  }

  handleNextClick(event) {
    event.preventDefault();
    console.log('➡️ Next button clicked');
    this.goToNextSlide();
  }

  handleTouchStart(event) {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  handleTouchMove(event) {
    this.touchEndX = event.changedTouches[0].screenX;
  }

  handleTouchEnd() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.goToNextSlide();
      } else {
        this.goToPreviousSlide();
      }
    }

    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  handleMouseEnter() {
    // Pysäytä CSS-animaatio
    if (this.navigation) {
      const activeButton = this.navigation.querySelector('button[aria-current="true"]');
      const progressCircle = activeButton?.querySelector('.circular-progress circle:last-child');
      if (progressCircle) {
        progressCircle.style.animationPlayState = 'paused';
      }
    }
  }

  handleMouseLeave() {
    if (this.hasAttribute('autoplay')) {
      // Jatka CSS-animaatiota siitä mihin se jäi
      if (this.navigation) {
        const activeButton = this.navigation.querySelector('button[aria-current="true"]');
        const progressCircle = activeButton?.querySelector('.circular-progress circle:last-child');
        if (progressCircle) {
          progressCircle.style.animationPlayState = 'running';
        }
      }
    }
  }

  goToSlide(index, animate = true) {
    if (index === this.currentIndex || index < 0 || index >= this.slides.length) {
      return;
    }

    const previousIndex = this.currentIndex;
    this.currentIndex = index;

    // Update slides
    this.updateSlides(previousIndex, index, animate);
    
    // Update dots
    this.updateDots(index);

    // Restart CSS-animaatio uudelle dotille
    if (this.isAutoplayActive && this.navigation) {
      const newActiveButton = this.navigation.querySelector(`button:nth-child(${index + 1})`);
      const progressCircle = newActiveButton?.querySelector('.circular-progress circle:last-child');
      if (progressCircle) {
        progressCircle.style.animation = 'none';
        void progressCircle.offsetHeight; // Force reflow
        progressCircle.style.animation = '';
      }
    }
  }

    updateSlides(previousIndex, newIndex, animate) {
    const previousSlide = this.slides[previousIndex];
    const newSlide = this.slides[newIndex];

    // Yksinkertainen vaihto ilman animaatiota
    previousSlide.classList.remove('is-selected');
    newSlide.classList.add('is-selected');
    }

  updateDots(index) {
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  goToNextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    console.log('➡️ Going to next slide:', nextIndex);
    this.goToSlide(nextIndex);
  }

  goToPreviousSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    console.log('⬅️ Going to previous slide:', prevIndex);
    this.goToSlide(prevIndex);
  }
}

// Register custom element
if (!window.customElements.get('slideshow-two-column')) {
  window.customElements.define('slideshow-two-column', SlideshowTwoColumn);
}